import {
    Box,
    Button,
    Divider,
    Flex,
    Grid,
    GridItem,
    HStack,
    Link,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    useColorModeValue,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { processesService } from "../../../services/processesService";
import { trafficViolationService } from "services/trafficViolationService";
import Card from "../../../components/Card/Card";
import CardHeader from "../../../components/Card/CardHeader";
import CardBody from "../../../components/Card/CardBody";
import TimelineRow from "../../../components/Tables/TimelineRow";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { getSneDate } from "../../../utils/traffic-violation-utils";
import { currentUserHasPermission } from "../../../utils/permission";
import { PERMISSIONS } from "../../../constants/permission";
import { getLimitDate } from "../../../utils/process-utils";
import Loading from "../../../components/Loading/Loading";
import { ROUTES } from "constants/routes-path";
import { FaBuilding, FaExclamationTriangle, FaFileSignature, FaGavel, FaRegClock, FaUserCheck } from "react-icons/fa";
import { MdArchive, MdMailOutline, MdNotificationsActive, MdPayment, MdPhoneAndroid } from "react-icons/md";
import { configService } from "../../../services/configService";

const hasMeaningfulText = (value) => value != null && String(value).trim().length > 0;

const ProcessDetail = ({ initialData = null }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [processData, setProcessData] = useState(undefined);
    const [timelineData, setTimelineData] = useState([]);
    const [isJudgementMode, setIsJudgementMode] = useState(false);
    const [attachmentUrl, setAttachmentUrl] = useState(null);
    const [imageBlobUrls, setImageBlobUrls] = useState([]);
    const textColor = useColorModeValue("gray.700", "white");
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isLoadingGetAitPdf, setIsLoadingGetAitPdf] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [relatedProcesses, setRelatedProcesses] = useState([]);
    const [processShowDentranSyncStatus, setProcessShowDentranSyncStatus] = useState(false);
    const showToast = useCustomToast();

    const processDetailsAbsoluteUrl = (processId) =>
        `${window.location.origin}${ROUTES.processesDetailsPath.replace(":id", String(processId))}`;

    const relatedProcessLabel = (p) => p?.numero ?? p?.nome ?? `#${p?.id}`;

    const tabs = useMemo(() => {
        const list = [
          { label: t("process.details.tab-general.title") },
          { label: t("process.details.tab-owner.title") },
          { label: t("process.details.tab-driver.title") },
          { label: t("process.details.tab-ait.title") },
          { label: t("process.details.tab-timeline.title") },
        ];
    
        if (processData?.infracao?.nome_condutor) {
          list.push({ label: t("process.details.tab-real-infractor.title") });
        }
    
        if (processData?.infracao?.images?.length > 0) {
          list.push({ label: t("process.details.tab-images.title") });
        }
    
        if (processData?.imagens) {
          list.push({ label: t("process.details.tab-attachments.title") });
        }
    
        if (processData?.decisao_antes_reexame) {
          list.push({ label: t("process.details.tab-previous-decision.title") });
        }
    
        if (isJudgementMode) {
          list.push({ label: t("process.details.tab-judgment-ait.title"), isAitPreview: true });
        }
    
        return list;
    }, [processData, isJudgementMode, t]);

    const aitTabIndex = useMemo(
        () => tabs.findIndex(tab => tab.isAitPreview),
        [tabs]
    );

    useEffect(() => {
        if (tabIndex === aitTabIndex && aitTabIndex !== -1) {
          handleGetAitPdfUrl(processData.id_infracao);
        }
    }, [tabIndex, aitTabIndex]);

    const iconMap = {
        "infraction-committed": FaExclamationTriangle,
        "infraction-recorded": FaFileSignature,
        "notification-issued": MdNotificationsActive,
        "notification-mailed": MdMailOutline,
        "penalty-issued": FaGavel,
        "penalty-mailed": MdMailOutline,
        "driver-indicated": FaUserCheck,
        "payment-confirmed": MdPayment,
        "process-created": FaFileSignature,
        "process-judgement": FaGavel,
        "process-judgement-pre-review": FaGavel,
        "sne-joined": MdPhoneAndroid,
        "defense-deadline": FaRegClock,
        "archived-by": MdArchive,
        "forwarded-to-person": FaUserCheck,
        "forwarded-to-unit": FaBuilding,
    };

    useEffect(() => {
        if (id || initialData) {
            fetchProcess();
        }
    }, [id, initialData]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cfg = await configService.get();
                if (!cancelled && cfg?.process_show_dentran_sync_status === true) {
                    setProcessShowDentranSyncStatus(true);
                }
            } catch {
                if (!cancelled) setProcessShowDentranSyncStatus(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const dentranSyncRows = useMemo(() => {
        if (!processData || !processShowDentranSyncStatus) {
            return null;
        }
        const rows = [];
        const respostaCriacao = processData.resposta_criacao_processo;
        const erroCriacao = processData.erro_criacao_processo;
        if (hasMeaningfulText(respostaCriacao)) {
            rows.push({ kind: "success", messageKey: "process.details.tab-general.dentran-sync-creation-success" });
        } else if (hasMeaningfulText(erroCriacao)) {
            rows.push({ kind: "error", message: erroCriacao });
        }
        const respostaResultado = processData.resposta_resultado_processo;
        const erroResultado = processData.erro_resultado_processo;
        if (hasMeaningfulText(respostaResultado)) {
            rows.push({ kind: "success", messageKey: "process.details.tab-general.dentran-sync-result-success" });
        } else if (hasMeaningfulText(erroResultado)) {
            rows.push({ kind: "error", message: erroResultado });
        }
        return rows.length > 0 ? rows : null;
    }, [processData, processShowDentranSyncStatus]);

    useEffect(() => {
        let cancelled = false;
        const loadRelatedByInfraction = async () => {
            if (!processData) return;

            const routeProcessId = id ?? initialData?.id;
            if (
                routeProcessId != null &&
                routeProcessId !== "" &&
                String(processData.id) !== String(routeProcessId)
            ) {
                setRelatedProcesses([]);
                return;
            }

            const idInfracao = processData.id_infracao;
            if (!idInfracao) {
                setRelatedProcesses([]);
                return;
            }
            try {
                const list = await processesService.listByInfractionId(idInfracao);
                if (cancelled) return;
                const currentId = String(processData.id ?? "");
                const filtered = list.filter((p) => p?.id != null && String(p.id) !== currentId);
                setRelatedProcesses(filtered);
            } catch {
                if (!cancelled) setRelatedProcesses([]);
            }
        };
        loadRelatedByInfraction();
        return () => {
            cancelled = true;
        };
    }, [processData?.id_infracao, processData?.id, id, initialData?.id, processData]);

    const fetchProcess = async () => {
        try {
            const process = initialData ?? await processesService.find(id);
            setIsJudgementMode(!!initialData);
            setProcessData(process);
            const timeline = getTimeline(process)
            setTimelineData(timeline);
            const blobUrl = base64PDFToBlobUrl(process.imagens);
            setAttachmentUrl(blobUrl);
            setImageBlobUrls(process.infracao.images);
        } catch (error) {
            console.error(t('process.messages.errors.list'), error);
        }
    }

    const handleGetAitPdfUrl = async (aitId) => {
        try {
            setIsLoadingGetAitPdf(true);

            const { data } = await trafficViolationService.printAit(aitId);
            const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            setPdfUrl(url); 
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        } finally {
            setIsLoadingGetAitPdf(false);
        }
    };
    //Quando é necessário carregar um pdf maior que 2MB então é necessário fazer esse parse, para renderizar corretamente
    //ou ocorre do elemento html ficar bloqueado.
    //https://stackoverflow.com/a/73283726/4598175
    const base64PDFToBlobUrl = (base64) => {
        const binStr = atob(base64);
        const len = binStr.length;
        const arr = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            arr[i] = binStr.charCodeAt(i);
        }
        const blob = new Blob([arr], { type: 'application/pdf' });
        return URL.createObjectURL(blob);
    }

    function getTimeline(process) {
        const timeline = [];
        const infracao = process.infracao;

        timeline.push({
            date: infracao.data_infracao,
            label: t('process.details.tab-timeline.infraction-committed'),
            logo: iconMap["infraction-committed"]
        });

        timeline.push({
            date: infracao.data_lavratura,
            label: t('process.details.tab-timeline.infraction-recorded'),
            logo: iconMap["infraction-recorded"]
        });

        const printWithoutRenainf = true;

        if ((printWithoutRenainf || (infracao.codigo_renainf && isNotBlank(infracao.resposta_criacao_notificacao_autuacao))) &&
            infracao.data_emissao_notificacao_autuacao) {
            timeline.push({
                date: infracao.data_emissao_notificacao_autuacao,
                label: t('process.details.tab-timeline.notification-issued'),
                logo: iconMap["notification-issued"]
            });
        }

        if (infracao.data_envio_notificacao_autuacao) {
            timeline.push({
                date: infracao.data_envio_notificacao_autuacao,
                label: t('process.details.tab-timeline.notification-mailed'),
                logo: iconMap["notification-mailed"]
            });
        }

        if ((printWithoutRenainf || (infracao.codigo_renainf && infracao.resposta_criacao_notificacao_penalidade)) &&
            infracao.data_emissao_notificacao_penalidade) {
            timeline.push({
                date: infracao.data_emissao_notificacao_penalidade,
                label: t('process.details.tab-timeline.penalty-issued'),
                logo: iconMap["penalty-issued"]
            });
        }

        if (infracao.data_envio_notificacao_penalidade) {
            timeline.push({
                date: infracao.data_envio_notificacao_penalidade,
                label: t('process.details.tab-timeline.penalty-mailed'),
                logo: iconMap["penalty-mailed"]
            });
        }

        if (infracao.data_indicacao_condutor) {
            timeline.push({
                date: infracao.data_indicacao_condutor,
                label: t('process.details.tab-timeline.driver-indicated'),
                logo: iconMap["driver-indicated"]
            });
        }

        if (infracao.data_pagamento) {
            timeline.push({
                date: infracao.data_pagamento,
                label: t('process.details.tab-timeline.payment-confirmed'),
                logo: iconMap["payment-confirmed"]
            });
        }

        timeline.push({
            date: process.data_criacao,
            label: t('process.details.tab-timeline.process-created'),
            logo: iconMap["process-created"]
        });

        if (!process.status === 'CREATED' && process.data_limite_julgamento && !process?.dataArquivamento) {
            timeline.push({
                date: process.data_limite_julgamento,
                label: t('process.details.tab-timeline.process-judgement'),
                logo: iconMap["process-judgement"]
            });
        }

        if (process.data_limite_julgamento_antes_reexame) {
            timeline.push({
                date: process.data_limite_julgamento_antes_reexame,
                label: t('process.details.tab-timeline.process-judgement-pre-review'),
                logo: iconMap["process-judgement-pre-review"]
            });
        }

        const sneDate = getSneDate(infracao);
        if (sneDate) {
            timeline.push({
                date: sneDate,
                label: t('process.details.tab-timeline.sne-joined'),
                logo: iconMap["sne-joined"]
            });
        }

        process.tramitacoes_processos.forEach(processRouting => {
            if (processRouting.data_criacao && processRouting?.usuario_responsavel_atual?.pessoa) {
                if (currentUserHasPermission([PERMISSIONS.PROCESS_ALLOCATION, PERMISSIONS.FILL_FINAL_DECISION, PERMISSIONS.REASSIGN_PROCESSES])) {
                    timeline.push({
                        date: processRouting.data_criacao,
                        label: t('process.details.tab-timeline.forwarded-to-person', {
                            name: processRouting.usuario_responsavel_atual.pessoa.nome,
                            sigla: processRouting.unidade_administrativa_responsavel.sigla
                        }),
                        logo: iconMap["forwarded-to-person"]
                    });
                } else {
                    timeline.push({
                        date: processRouting.data_criacao,
                        label: t('process.details.tab-timeline.forwarded-to-unit', {
                            sigla: processRouting.unidade_administrativa_responsavel.sigla
                        }),
                        logo: iconMap["forwarded-to-unit"]
                    });
                }
            }
        });

        if (getLimitDate(process)) {
            timeline.push({
                date: getLimitDate(process),
                label: t('process.details.tab-timeline.defense-deadline'),
                logo: iconMap["defense-deadline"]
            });
        }

        if (process.data_arquivamento) {
            let label = t('process.details.tab-timeline.archived-by', {
                name: process.nome_pessoa_arquivamento
            });
            if (process.motivo_arquivamento) {
                label += ': ' + process.motivoArquivamento;
            }
            timeline.push({
                date: process.data_arquivamento,
                label,
                logo: iconMap["archived-by"]
            });
        }

        timeline.sort((a, b) => {
            const parseDate = (str) => {
                if (!str) {
                    return
                }
                const [datePart, timePart] = str.split(' ');
                const [day, month, year] = datePart.split('/');
                return new Date(`${year}-${month}-${day}T${timePart}`);
            };

            return parseDate(a.date) - parseDate(b.date);
        });

        return timeline;
    }

    return (
        <Flex w="100%" direction="column" pt={{ sm: "125px", lg: "75px" }}>
            <Card px="0px" mb="24px">
                <CardHeader px="22px" mb="24px">
                    <Flex direction="column">
                        {isJudgementMode ? (
                            <Text color={textColor} fontSize="lg" mb="6px">
                                <Text as="span" fontWeight="bold">
                                    {t("process.details.title")}:
                                </Text>{"\u00A0"}
                                <Text as="span" fontWeight="normal">
                                    {processData?.numero ?? "-"}
                                </Text>
                            </Text>
                        ) : (
                            <Text color={textColor} fontSize="lg" mb="6px">
                                <Text as="span" fontWeight="normal">
                                        {t("process.details.title")} -
                                </Text>{"\u00A0"}
                                <Text as="span" fontWeight="bold">
                                        {processData && processData.numero ? processData.numero : ""}
                                </Text>
                            </Text>
                        )}
                    </Flex>
                </CardHeader>
                <CardBody px="22px">
                    {
                        processData ? (
                            <>
                            <Tabs variant="enclosed" index={tabIndex} onChange={setTabIndex}>
                                <TabList overflowX="auto" whiteSpace="nowrap" sx={{ "&::-webkit-scrollbar": { display: "none" } }}>
                                    {tabs.map((tab, index) => (
                                    <Tab key={index}>{tab.label}</Tab>
                                    ))}
                                </TabList>
                                <TabPanels>
                                    <TabPanel>
                                        {!isJudgementMode &&
                                            <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                                <CardHeader>
                                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                                        {t("process.details.tab-general.title-process")}
                                                    </Text>
                                                </CardHeader>
                                                <Divider borderColor="#3884cc" mb="20px" />
                                                <CardBody>
                                                    <Stack spacing={4}>
                                                        <Grid
                                                            gap="6px 0"
                                                            templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
                                                        >
                                                            <GridItem fontSize="md" color={textColor}>
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-general.number")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.numero ?? "-"}
                                                                </Text>
                                                            </GridItem>
                                                            <GridItem fontSize="md" color={textColor}>
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-general.status")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.status ?? "-"}
                                                                </Text>
                                                            </GridItem>
                                                        </Grid>
                                                        <Grid>
                                                        <GridItem fontSize="md" color={textColor}>
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-general.created-by")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.usuario_atendente?.pessoa?.nome ?? "-"}
                                                                </Text>
                                                            </GridItem>
                                                            <GridItem fontSize="md" color={textColor}>
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-general.responsable-person")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.usuario_responsavel_atual?.pessoa?.nome ?? "-"}
                                                                </Text>
                                                            </GridItem>
                                                        </Grid>

                                                        {processData?.process_responsible_needed && (
                                                            <>
                                                                <Grid gap="6px">
                                                                    <Text color={textColor} fontSize="md">
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("process.details.tab-general.facts")}:
                                                                        </Text>{"\u00A0"}
                                                                        {processData?.fatos ?? "-"}
                                                                    </Text>
                                                                </Grid>
                                                                <Grid gap="6px">
                                                                    <Text color={textColor} fontSize="md">
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("process.details.tab-general.fundaments")}:
                                                                        </Text>{"\u00A0"}
                                                                        {processData?.fundamentos ?? "-"}
                                                                    </Text>
                                                                </Grid>
                                                                <Grid gap="6px">
                                                                    <Text color={textColor} fontSize="md">
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("process.details.tab-general.decision")}:
                                                                        </Text>{"\u00A0"}
                                                                        {processData?.decisao ?? "-"}
                                                                    </Text>
                                                                </Grid>
                                                            </>
                                                        )}

                                                        {processData?.process_judgement_meetings_needed && (
                                                            <Grid
                                                                gap="6px 0"
                                                                templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }}
                                                            >
                                                                <GridItem fontSize="md" color={textColor}>
                                                                    <Text>
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("process.details.tab-general.relator-decision")}:
                                                                        </Text>{"\u00A0"}
                                                                        {processData?.decisao_relator ?? "-"}
                                                                    </Text>
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor}>
                                                                    <Text>
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("process.details.tab-general.judgment-deadline")}:
                                                                        </Text>{"\u00A0"}
                                                                        {processData?.data_limite_julgamento ?? "-"}
                                                                    </Text>
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor}>
                                                                    <Text>
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("process.details.tab-general.voting")}:
                                                                        </Text>{"\u00A0"}
                                                                        {processData?.votacao ?? "-"}
                                                                    </Text>
                                                                </GridItem>
                                                            </Grid>
                                                        )}
                                                    </Stack>
                                                </CardBody>
                                            </Card>
                                        }
                                        <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                            <CardHeader>
                                                <Text color={textColor} fontSize="md" fontWeight="bold">
                                                    {t("process.details.tab-general.title-ait")}
                                                </Text>
                                            </CardHeader>
                                            <Divider borderColor="#3884cc" mb="20px" />
                                            <CardBody>
                                                <Stack spacing={4}>
                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(2, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.ait-number")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.numero_ait ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.status")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.situacao ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>

                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(2, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.batch")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.nome_lote ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.renainf-code")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.codigo_renainf ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>
                                                </Stack>
                                            </CardBody>
                                        </Card>
                                        <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                            <CardHeader>
                                                <Text color={textColor} fontSize="md" fontWeight="bold">
                                                    {t("process.details.tab-general.title-vehicle")}
                                                </Text>
                                            </CardHeader>
                                            <Divider borderColor="#3884cc" mb="20px" />
                                            <CardBody>
                                                <Stack spacing={4}>
                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <HStack spacing="20px">
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-general.vehicle-plate")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.infracao?.placa_veiculo ?? "-"}
                                                                </Text>
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-general.vehicle-uf")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.infracao?.uf_placa_veiculo ?? "-"}
                                                                </Text>
                                                            </HStack>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.vehicle-color")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.descricao_cor_veiculo ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.vehicle-category")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.descricao_categoria_veiculo ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>

                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.vehicle-brand")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.descricao_marca_modelo_veiculo ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.vehicle-kind")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.descricao_especie_veiculo ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.vehicle-type")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.descricao_tipo_veiculo ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>
                                                </Stack>
                                            </CardBody>
                                        </Card>
                                        {relatedProcesses.length > 0 && (
                                            <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                                <CardHeader>
                                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                                        {t("process.details.tab-general.title-related-processes")}
                                                    </Text>
                                                </CardHeader>
                                                <Divider borderColor="#3884cc" mb="20px" />
                                                <CardBody>
                                                    <Stack spacing={3}>
                                                        <Text fontSize="sm" color="gray.500">
                                                            {t("process.details.tab-general.related-processes-hint")}
                                                        </Text>
                                                        <Stack spacing={2}>
                                                            {relatedProcesses.map((p) => (
                                                                <Link
                                                                    key={p.id}
                                                                    href={processDetailsAbsoluteUrl(p.id)}
                                                                    isExternal
                                                                    color="blue.500"
                                                                    fontWeight="medium"
                                                                    display="inline-flex"
                                                                    alignItems="center"
                                                                    gap={1}
                                                                >
                                                                    {relatedProcessLabel(p)}
                                                                    <ExternalLinkIcon boxSize={3.5} aria-hidden />
                                                                </Link>
                                                            ))}
                                                        </Stack>
                                                    </Stack>
                                                </CardBody>
                                            </Card>
                                        )}
                                        {dentranSyncRows && (
                                            <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                                <CardHeader>
                                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                                        {t("process.details.tab-general.title-dentran-sync")}
                                                    </Text>
                                                </CardHeader>
                                                <Divider borderColor="#3884cc" mb="20px" />
                                                <CardBody>
                                                    <Stack spacing={3}>
                                                        {dentranSyncRows.map((row, idx) => (
                                                            <Text
                                                                key={idx}
                                                                fontSize="md"
                                                                whiteSpace="pre-wrap"
                                                                color={row.kind === "success" ? "green.600" : "red.600"}
                                                            >
                                                                {row.kind === "success" ? t(row.messageKey) : row.message}
                                                            </Text>
                                                        ))}
                                                    </Stack>
                                                </CardBody>
                                            </Card>
                                        )}
                                    </TabPanel>
                                    <TabPanel>
                                        <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                            <CardHeader>
                                                <Text color={textColor} fontSize="md" fontWeight="bold">
                                                    {t("process.details.tab-owner.title")}
                                                </Text>
                                            </CardHeader>
                                            <Divider borderColor="#3884cc" mb="20px" />
                                            <CardBody>
                                                <Stack spacing={4}>
                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-owner.owner-name")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.nome_proprietario_veiculo_ait ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-owner.cpf-and-cnpj")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.cpf_cnpj_proprietario_veiculo_ait ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.drivers-licence")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.cnh_proprietario_veiculo ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>

                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }}
                                                    >
                                                        <GridItem
                                                            fontSize="md"
                                                            color={textColor}
                                                            colSpan={{ base: 1, md: 2 }}
                                                        >
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-owner.address")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.logradouro_proprietario_veiculo_ait ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.complement")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.complemento_imovel_proprietario_veiculo_ait ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>

                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.neighbour")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.bairro_imovel_proprietario_veiculo_ait ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <HStack spacing="20px" align="center">
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-general.city")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.infracao?.codigo_municipio_imovel_proprietario_veiculo_ait ?? "-"}
                                                                </Text>
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-general.vehicle-uf")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.infracao?.uf_imovel_proprietario_veiculo_ait ?? "-"}
                                                                </Text>
                                                            </HStack>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.street-code")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.cep_imovel_proprietario_veiculo_ait ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>
                                                </Stack>
                                            </CardBody>
                                        </Card>
                                    </TabPanel>
                                    <TabPanel>
                                        <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                            <CardHeader>
                                                <Text color={textColor} fontSize="md" fontWeight="bold">
                                                    {t("process.details.tab-driver.title")}
                                                </Text>
                                            </CardHeader>
                                            <Divider borderColor="#3884cc" mb="20px" />
                                            <CardBody>
                                                <Stack spacing={4}>
                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.driver-name")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.nome_condutor ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.driver-cpf")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.cpf_cnpj_condutor ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>
                                                </Stack>
                                            </CardBody>
                                        </Card>
                                    </TabPanel>
                                    <TabPanel>
                                        <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                            <CardHeader>
                                                <Text color={textColor} fontSize="md" fontWeight="bold">
                                                    {t("process.details.tab-ait.subtitle-ait")}
                                                </Text>
                                            </CardHeader>
                                            <Divider borderColor="#3884cc" mb="20px" />
                                            <CardBody>
                                                <Stack spacing={4}>
                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <HStack flexWrap="wrap" spacing={{base:"5px", md: "20px"}} align="center">
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-ait.ait-code")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.infracao?.codigo_tipo_infracao ?? "-"}
                                                                </Text>
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-ait.unfolding")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.infracao?.desdobramento_tipo_infracao ?? "-"}
                                                                </Text>
                                                            </HStack>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.severity")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.gravidade_tipo_infracao ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.fine-score")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.pontuacao_tipo_infracao ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>

                                                    <Grid gap="6px 0" templateColumns="1fr">
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.description")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.descricao_tipo_infracao ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>

                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.value")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.valor_tipo_infracao ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.agent-code")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.codigo_agente ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.ctb-article")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.amparo_legal ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>

                                                    {isJudgementMode && (
                                                        <Grid
                                                            gap="6px"
                                                            templateColumns={
                                                                processData?.infracao?.medidas_administrativas ? "1fr 1fr" : "1fr"
                                                            }
                                                        >
                                                            <GridItem fontSize="md" color={textColor}>
                                                                <Text>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("process.details.tab-ait.observations")}:
                                                                    </Text>{"\u00A0"}
                                                                    {processData?.infracao?.observacoes ?? "-"}
                                                                </Text>
                                                            </GridItem>

                                                            {processData?.infracao?.medidas_administrativas && (
                                                                <GridItem fontSize="md" color={textColor}>
                                                                    <Text>
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("process.details.tab-ait.administrative-measures")}:
                                                                        </Text>{"\u00A0"}
                                                                        {processData?.infracao?.medidas_administrativas ?? "-"}
                                                                    </Text>
                                                                </GridItem>
                                                            )}
                                                        </Grid>
                                                    )}
                                                </Stack>
                                            </CardBody>
                                        </Card>
                                        <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                            <CardHeader>
                                                <Text color={textColor} fontSize="md" fontWeight="bold">
                                                    {t("process.details.tab-ait.title-commitment")}
                                                </Text>
                                            </CardHeader>
                                            <Divider borderColor="#3884cc" mb="20px" />
                                            <CardBody>
                                                <Stack spacing={4}>
                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns="1fr"
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-owner.address")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.logradouro_local_infracao ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>

                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-general.complement")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.complemento_local_infracao ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.date-time")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.data_infracao ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>
                                                </Stack>
                                            </CardBody>
                                        </Card>
                                        <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                            <CardHeader>
                                                <Text color={textColor} fontSize="md" fontWeight="bold">
                                                    {t("process.details.tab-ait.title-measurement")}
                                                </Text>
                                            </CardHeader>
                                            <Divider borderColor="#3884cc" mb="20px" />
                                            <CardBody>
                                                <Stack spacing={4}>
                                                    <Grid
                                                        gap="6px 0"
                                                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                                                    >
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.measurement-value")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.valor_medicao_real ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.measurement-limit-value-allowed")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.valor_limite_permitido ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text>
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("process.details.tab-ait.measurement-limit-value-considered")}:
                                                                </Text>{"\u00A0"}
                                                                {processData?.infracao?.valor_limite_considerado ?? "-"}
                                                            </Text>
                                                        </GridItem>
                                                    </Grid>
                                                </Stack>
                                            </CardBody>
                                        </Card>
                                    </TabPanel>
                                    <TabPanel>
                                        <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                            <Text fontSize="md" fontWeight="bold" color={textColor}>
                                                {t("process.details.tab-timeline.title")}
                                            </Text>
                                            <Divider borderColor="#3884cc" />
                                            <Box m="4">
                                                {timelineData.length > 0 && (
                                                    <Stack>
                                                        {timelineData.map((entry, index) => {
                                                            return (
                                                                <TimelineRow
                                                                    key={index}
                                                                    logo={entry.logo ?? null}
                                                                    title={entry.label}
                                                                    titleColor={textColor}
                                                                    date={entry.date}
                                                                    index={index + 1}
                                                                    arrLength={timelineData.length + 1}
                                                                    isDark={false}
                                                                />
                                                            );
                                                        })}
                                                    </Stack>
                                                )}
                                            </Box>
                                        </Card>
                                    </TabPanel>
                                    {processData?.infracao?.nome_condutor && (
                                        <TabPanel>
                                            <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                                <CardHeader>
                                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                                        {t("process.details.tab-real-infractor.subtitle")}
                                                    </Text>
                                                </CardHeader>
                                                <Divider borderColor="#3884cc" mb="20px" />
                                                <CardBody>
                                                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="6px 0">
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-real-infractor.driver-name")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.infracao?.nome_condutor ?? "-"}
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-real-infractor.driver-licence")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.infracao?.cnh_indicacao_condutor ?? "-"}
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-real-infractor.register-number")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.infracao?.numero_registro_indicacao_condutor ?? "-"}
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-real-infractor.process-number")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.infracao?.numero_processo_indicacao_condutor ?? "-"}
                                                        </GridItem>
                                                    </Grid>
                                                </CardBody>
                                            </Card>
                                        </TabPanel>
                                    )}
                                    {imageBlobUrls.length > 0 && (
                                        <TabPanel>
                                            <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                                <CardHeader>
                                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                                        {t("process.details.tab-images.title")}
                                                    </Text>
                                                </CardHeader>
                                                <Divider borderColor="#3884cc" mb="20px" />
                                                <CardBody>
                                                    <Box
                                                        h={{ base: "62vh", md: "68vh" }}
                                                        overflow="hidden"
                                                    >
                                                        <Grid
                                                            gap="10px"
                                                            h="100%"
                                                            templateColumns={{
                                                                base: "1fr",
                                                                md: imageBlobUrls?.length === 1
                                                                    ? "1fr"
                                                                    : "repeat(" + imageBlobUrls?.length + ", 1fr)"
                                                            }}
                                                            alignItems="center"
                                                        >
                                                            {imageBlobUrls?.map((src, idx) => (
                                                                <Box
                                                                    key={idx}
                                                                    h="100%"
                                                                    overflow="hidden"
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    justifyContent="center"
                                                                    cursor="pointer"
                                                                    onClick={() => setImagePreviewUrl(src)}
                                                                >
                                                                    <img
                                                                        src={src}
                                                                        alt={`Foto ${idx + 1}`}
                                                                        style={{
                                                                            maxWidth: "100%",
                                                                            maxHeight: "100%",
                                                                            width: "auto",
                                                                            height: "auto",
                                                                            objectFit: "contain"
                                                                        }}
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Grid>
                                                    </Box>
                                                </CardBody>
                                            </Card>
                                        </TabPanel>
                                    )}
                                    {attachmentUrl && (
                                        <TabPanel>
                                            <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                                <CardHeader>
                                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                                        {t("process.details.tab-attachments.title")}
                                                    </Text>
                                                </CardHeader>
                                                <Divider borderColor="#3884cc" mb="20px" />
                                                <CardBody>
                                                    <Box
                                                        as="iframe"
                                                        src={attachmentUrl}
                                                        width="100%"
                                                        height="600px"
                                                        border="0"
                                                    />
                                                </CardBody>
                                            </Card>
                                        </TabPanel>
                                    )}
                                    {processData?.decisao_antes_reexame && (
                                        <TabPanel>
                                            <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                                <CardHeader>
                                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                                        {t("process.details.tab-previous-decision.title")}
                                                    </Text>
                                                </CardHeader>
                                                <Divider borderColor="#3884cc" mb="20px" />
                                                <CardBody>
                                                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="6px 0">
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-general.decision")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.decisao_antes_reexame ?? "-"}
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-general.responsable-person")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.responsavel_antes_reexame?.pessoa?.nome ?? "-"}
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-general.facts")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.fatos_antes_reexame ?? "-"}
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-general.fundaments")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.fundamentos_antes_reexame ?? "-"}
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-ait.reporter-decision")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.decisao_relator_antes_reexame ?? "-"}
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-ait.judgement-date")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.data_limite_julgamento_antes_reexame ?? "-"}
                                                        </GridItem>
                                                        <GridItem fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("process.details.tab-ait.voting")}:
                                                            </Text>{"\u00A0"}
                                                            {processData?.votacao_antes_reexame ?? "-"}
                                                        </GridItem>
                                                    </Grid>
                                                    {processData?.arquivo_antes_reexame_existe && (
                                                        <Box mt="6">
                                                            <Box as="iframe"
                                                                src={`/painel/gerencia-transportes/processos/anexo-anterior/${processData?.id}?${processData?.arquivo_antes_reexame_data_alteracao}`}
                                                                width="100%"
                                                                height="600px"
                                                                border="0"
                                                            />
                                                        </Box>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </TabPanel>
                                    )}
                                    {isJudgementMode && processData?.id_infracao &&
                                        <TabPanel>
                                            <Card border="1px solid #3884cc" bg={cardBg} mb="30px">
                                                {(pdfUrl && !isLoadingGetAitPdf) ? (
                                                    <iframe
                                                        src={pdfUrl}
                                                        width="100%"
                                                        height="600px"
                                                        style={{ border: 'none' }}
                                                    />
                                                ) : (
                                                    <Loading maxH={{ sm: "100px", md: "350px" }} />
                                                )}
                                            </Card>
                                        </TabPanel>
                                    }
                                </TabPanels>
                            </Tabs>
                            <Modal
                                isOpen={!!imagePreviewUrl}
                                onClose={() => setImagePreviewUrl(null)}
                                size="6xl"
                                scrollBehavior="inside"
                            >
                                <ModalOverlay />
                                <ModalContent maxH="90vh" bg={cardBg}>
                                    <ModalCloseButton />
                                    <ModalBody p={0} display="block">
                                        {imagePreviewUrl && (
                                            <img
                                                src={imagePreviewUrl}
                                                alt=""
                                                style={{
                                                    width: "100%",
                                                    height: "auto",
                                                    display: "block",
                                                    verticalAlign: "top"
                                                }}
                                            />
                                        )}
                                    </ModalBody>
                                </ModalContent>
                            </Modal>
                            </>
                        ) : (
                            <Loading maxH={{ sm: "100px", md: "350px" }} />
                        )
                    }
                    <Flex justifyContent="flex-start">
                        <Button
                            variant="light"
                            fontSize="sm"
                            alignSelf="flex-end"
                            mt="24px"
                            w="125px"
                            onClick={() => navigate(-1)}
                        >
                            {t("system.actions.back")}
                        </Button>
                    </Flex>
                </CardBody>
            </Card>
        </Flex>
    )
}

export default ProcessDetail;
